// ═══════════════════════════════════════════════════════════════
// FolderTemplateV2Button.tsx – „Mappasablon 2026 v2 (mintafájlokkal)” letöltése és beállítása
//
// A 2026-os üres mappaszerkezet, a pontok mappáiban a superadmin által kijelölt mintaszervezet
// sablonfájljaival (pl. a kötegelt feltöltéssel betöltött „1_08_…docx” minták). Bárki letöltheti, ha be van
// állítva; a forrás szervezetet csak a superadmin választja ki.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { ApiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { Download, Settings2, RefreshCw } from 'lucide-react';

export const FolderTemplateV2Button: React.FC = () => {
  const { role } = useAuth();
  const isSuper = String(role) === 'superadmin';
  const [cfg, setCfg] = useState<Awaited<ReturnType<typeof ApiClient.getFolderTemplateV2>>['data'] | null>(null);
  const [open, setOpen] = useState(false);
  const [tenants, setTenants] = useState<{ id: number; name: string }[]>([]);
  const [sel, setSel] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    const r = await ApiClient.getFolderTemplateV2().catch(() => null);
    if (r?.ok && r.data) { setCfg(r.data); setSel(r.data.source_tenant_id ? String(r.data.source_tenant_id) : ''); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (open && isSuper && !tenants.length) ApiClient.getTenants().then((r: any) => { if (r?.ok) setTenants((r.data || []).map((t: any) => ({ id: t.id, name: t.name }))); }).catch(() => {});
  }, [open]);

  const download = async () => {
    setBusy(true); setMsg(null);
    try { await ApiClient.downloadFolderTemplateZip({ blank: true, variant: 'v2' }); }
    catch (e: any) { setMsg(e.message || 'A letöltés nem sikerült'); }
    setBusy(false);
  };
  const save = async () => {
    setBusy(true); setMsg(null);
    const r = await ApiClient.setFolderTemplateV2(sel ? Number(sel) : null);
    setBusy(false);
    if (r.ok) { setMsg('Mentve.'); load(); } else setMsg(r.error || 'Nem sikerült menteni');
  };

  if (!cfg) return null;
  if (!cfg.configured && !isSuper) return null;
  return (
    <div className="relative inline-flex items-center gap-1">
      {cfg.configured && (
        <button type="button" onClick={download} disabled={busy}
          className="px-3.5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
          title={`A 2026-os mappaszerkezet mintadokumentumokkal${cfg.stats ? ` (${cfg.stats.files} fájl)` : ''}`}>
          {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          <span>{cfg.label} (mintafájlokkal)</span>
        </button>
      )}
      {isSuper && (
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} title="Mintafájlos mappasablon beállítása (superadmin)"
          className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
          <Settings2 className="w-4 h-4" />
        </button>
      )}
      {open && isSuper && (
        <div className="absolute right-0 top-full mt-2 z-20 w-80 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl text-xs space-y-2">
          <div className="font-bold text-slate-900 dark:text-white">Mappasablon 2026 v2 – mintafájlok forrása</div>
          <p className="text-slate-500 dark:text-slate-400">A kijelölt szervezet pontjaihoz csatolt fájlok kerülnek a 2026-os üres mappaszerkezet pontmappáiba. Bárki letöltheti.</p>
          <select value={sel} onChange={(e) => setSel(e.target.value)} aria-label="Forrás szervezet"
            className="w-full px-2.5 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white">
            <option value="">— nincs (kikapcsolva) —</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {cfg.stats && <div className="text-slate-500">Jelenleg: {cfg.stats.tenant_name} · {cfg.stats.files} fájl, {cfg.stats.points} ponton</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 cursor-pointer">Bezárás</button>
            <button type="button" onClick={save} disabled={busy} className="px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold cursor-pointer disabled:opacity-50">Mentés</button>
          </div>
        </div>
      )}
      {msg && <span role="status" className="text-[11px] text-slate-600 dark:text-slate-400 ml-1">{msg}</span>}
    </div>
  );
};

export default FolderTemplateV2Button;
